import { useState, useEffect } from "react";
import { fetchProductionMilestones } from "../../utils/dataReceiver";

/**
 * Fetch and manage state for the Production Milestones page-load data: the
 * five top-metric KPI cards and the Monthly Production Trend chart's default
 * (current-month) view.
 *
 * ── Scope ──────────────────────────────────────────────────────────────────
 *
 * This hook owns ONLY the page-load bundle from the `production-milestones`
 * route — the 5 KPIs and the bundled trend chart for the current month. It
 * does NOT own:
 *
 *   • Weekly Mod Rolls filtered data         → useWeeklyRolls.js
 *   • Monthly Production Trend filtered data → useMonthlyTrend.js
 *   • location.state (rollDate / openModal) navigation handling
 *
 * Those concerns are deliberately kept separate (see "Why" notes below) so
 * each piece of the page can load, fail, and re-fetch independently — a slow
 * or failed weekly-rolls filter shouldn't block the KPI cards from rendering,
 * and vice versa.
 *
 * ── Why location.state stays out of this hook ─────────────────────────────
 *
 * ProductionMilestonesPage resolves `location.state?.rollDate` (set when
 * arriving via navigate(..., { state: { rollDate } }) from the ModRollTime
 * modal) synchronously during render, BEFORE any hook runs, so the very
 * first paint already shows the correct filtered week — see `_initRoll` on
 * the page. That resolution produces the `{ year, monthIdx, weekKey }` trio
 * the Weekly Rolls filter needs.
 *
 * This hook's data (the 5 KPIs + trend chart) is NOT affected by which week
 * was deep-linked — the KPIs always reflect "right now" regardless of which
 * historical week someone clicked into from the modal. So there is nothing
 * for this hook to do with location.state; it fires once on mount with no
 * params, same as useHomepageData.
 *
 * ── Caching ─────────────────────────────────────────────────────────────────
 *
 * Mirrors the module-level cache pattern from useHomepageData.js: a quick
 * navigate-away / navigate-back within the TTL window serves cached data
 * instead of re-fetching. TTL is shorter here (30s vs homepage's 60s) since
 * production-milestones data — especially dailyModules — changes more
 * frequently throughout a shift.
 *
 * @returns {{
 *   metrics: {
 *     ytdModules:    { value: number, target: number },
 *     variancePct:   { value: number, target: number },
 *     weeklySqft:    { value: number, target: number },
 *     firstPassRate: { value: number, target: number },
 *     dailyModules:  { value: number, target: number },
 *   },
 *   trendChart: {
 *     month: string,
 *     days: Array<{ day: number, date: string, sqft: number|null, personnel: number|null }>,
 *     monthAvgSqft: number,
 *     monthAvgPersonnel: number,
 *   },
 *   loading: boolean,
 *   error:   string | null,
 *   refetch: () => void,
 * }}
 */

// ── Module-level cache ────────────────────────────────────────────────────────
// Shared across all ProductionMilestonesPage mounts within the same browser
// session so navigating away and back within the TTL window doesn't re-fetch.
let _cache = null;
let _cacheTime = 0;
const CACHE_TTL_MS = 30_000; // 30s — shorter than homepage's 60s; dailyModules
// and weeklySqft can change within a single shift.

// ── Safe defaults ─────────────────────────────────────────────────────────────
// Returned during the loading phase so KPI cards and the chart never receive
// undefined fields and can render their skeleton/zero state immediately.
const DEFAULT_STATE = {
  metrics: {
    ytdModules: { value: 0, target: 0 },
    variancePct: { value: 0, target: 95 },
    weeklySqft: { value: 0, target: 10_500 },
    firstPassRate: { value: 0, target: 95 },
    dailyModules: { value: 0, target: 2 },
  },
  trendChart: {
    month: "",
    days: [],
    monthAvgSqft: 0,
    monthAvgPersonnel: 0,
  },
};

const useProductionMilestones = () => {
  const [data, setData] = useState(_cache ?? DEFAULT_STATE);
  const [loading, setLoading] = useState(!_cache);
  const [error, setError] = useState(null);

  // Bumping this triggers the effect below to re-run, bypassing the cache —
  // used by the exposed `refetch()` function (e.g. a manual refresh button,
  // or after the Goals modal saves new targets that should reflect immediately).
  const [refetchTick, setRefetchTick] = useState(0);

  useEffect(() => {
    const controller = new AbortController();

    // refetchTick > 0 means refetch() was called explicitly — always bypass
    // the cache in that case, even if it's still "fresh" by TTL.
    const isStale =
      refetchTick > 0 || !_cache || Date.now() - _cacheTime > CACHE_TTL_MS;

    if (!isStale) {
      // Serve from cache without flipping loading — avoids a flash of the
      // loading state when the cache is still warm.
      return;
    }

    const load = async () => {
      setLoading(true);
      try {
        const result = await fetchProductionMilestones({}, controller.signal);
        _cache = result;
        _cacheTime = Date.now();
        setData(result);
        setError(null);
      } catch (err) {
        if (err.name === "AbortError") return;
        setError(err.message ?? "Failed to load production milestones data.");
      } finally {
        setLoading(false);
      }
    };

    load();
    return () => controller.abort();
  }, [refetchTick]);

  /**
   * Force a fresh fetch, bypassing the module-level cache regardless of TTL.
   * Useful after an action that's known to change the underlying data (e.g.
   * saving new production goals) so the KPI cards reflect it without waiting
   * for the cache to expire or the user to navigate away and back.
   */
  const refetch = () => setRefetchTick((t) => t + 1);

  return { ...data, loading, error, refetch };
};

export default useProductionMilestones;
